import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { ModalPortal } from '../../components';
import routeConfiguration from '../../routeConfiguration';
import { createResourceLocatorString } from '../../util/routes';
import { CONFIRM_PHONE_NUMBER, ENTER_PHONE_NUMBER, PHONE_NUMBER_CONFIRMED } from './helpers';
import StepConfirmPhoneNumber from './StepConfirmPhoneNumber';
import StepPhoneNumberConfirmed from './StepPhoneNumberConfirmed';
import StepEnterPhoneNumber from './StepEnterPhoneNumber';
import css from './MissingPhoneNumberModal.css';
import { useIntl } from 'react-intl';
import {
    verifyPhoneNumber as verifyPhoneNumberThunk,
    sendVerificationOtp,
} from '../../containers/ContactDetailsPage/ContactDetailsPage.duck';
import { initPhoneCode } from '../../forms/ContactDetailsForm/util/initPhoneCode';
import { saveContactDetails as saveContactDetailsThunk } from '../../containers/ContactDetailsPage/ContactDetailsPage.duck';
import { format } from '../FieldPhoneNumberInput/fiFormatter';

const MissingPhoneNumberModal = ({
    id = 'MissingPhoneNumberModal',
    initialState,
    isOpen,
    onClose,
    missingPhoneNumberDescriptionId = 'missingPhoneNumberDescription',
    redirectOnCLose = false,
}) => {
    const currentUser = useSelector(state => state.user.currentUser);
    const saveContactDetailsInProgress = useSelector(
        s => s.ContactDetailsPage.saveContactDetailsInProgress
    );
    const savePhoneNumberError = useSelector(s => s.ContactDetailsPage.savePhoneNumberError);
    const sendVerificationOtpError = useSelector(
        s => s.ContactDetailsPage.sendVerificationOtpError
    );

    const history = useHistory();
    const intl = useIntl();

    const dispatch = useDispatch();

    const onResendVerificationOtp = phoneNumber => dispatch(sendVerificationOtp(phoneNumber));
    const onVerifyPhoneNumber = values => dispatch(verifyPhoneNumberThunk(values));
    const onSubmitContactDetails = values => dispatch(saveContactDetailsThunk(values));

    const { attributes } = currentUser || {};
    const {
        profile: { publicData, protectedData },
        email,
    } = attributes || {};

    const currentPhoneNumberVerified = publicData?.phoneVerified;
    const currentPhoneNumber = protectedData?.phoneNumber;
    const countryCode = initPhoneCode(currentPhoneNumber?.substring(0, 3), currentUser);
    const currentEmail = currentUser ? email : null;

    const initialStateMaybe =
        initialState || (currentPhoneNumber && CONFIRM_PHONE_NUMBER) || ENTER_PHONE_NUMBER;

    const [verificationState, setVerificationState] = useState(initialStateMaybe);
    const [otpCode, setOtpCode] = useState(null);
    const [phoneVerificationError, setPhoneVerificationError] = useState(null);

    const initialPhoneNumber = currentPhoneNumber ? currentPhoneNumber.replace(/\+\d\d/, '') : null;

    const handleCompleteProfileDismiss = () =>
        history.push(createResourceLocatorString('LandingPage', routeConfiguration(), {}, {}));

    const sendOtp = async ({ phoneNumber, countryCode }) => {
        const phoneNumberFormatted = countryCode.toString() + phoneNumber.replace(/\D/g, '');
        const otp = await onResendVerificationOtp(phoneNumberFormatted);

        setOtpCode(otp);
        setVerificationState(CONFIRM_PHONE_NUMBER);
    };

    const handleSubmitPhoneNumberForm = async values => {
        const phoneNumber =
            values.phoneNumber && values.countryCode
                ? `${values.countryCode}${format(values.phoneNumber, values.countryCode)}`
                : null;

        await onSubmitContactDetails({ ...values, phoneNumber, currentEmail, currentPhoneNumber });
        await sendOtp(values);
    };

    const verifyPhoneNumber = () => {
        const phoneNumberToVerify =
            currentPhoneNumber && countryCode
                ? currentPhoneNumber.startsWith(countryCode)
                    ? currentPhoneNumber
                    : `${countryCode}${currentPhoneNumber.replace(/\D/g, '')}`
                : currentPhoneNumber;

        if (!phoneNumberToVerify) {
            setPhoneVerificationError(
                intl.formatMessage({ id: 'MissingPhoneNumberModal.phoneNumberError' })
            );
            return;
        }

        setPhoneVerificationError(null);
        onVerifyPhoneNumber({
            phoneNumber: phoneNumberToVerify,
            phoneVerified: true,
        })
            .then(() => setVerificationState(PHONE_NUMBER_CONFIRMED))
            .catch(e => {
                setPhoneVerificationError(
                    e.message ||
                        e.data ||
                        intl.formatMessage({ id: 'MissingPhoneNumberModal.phoneNumberError' })
                );
            });
    };

    const handleReSendOtp = async () => {
        await sendOtp({
            countryCode: countryCode,
            phoneNumber: currentPhoneNumber,
        });
    };

    const stateConfig = {
        [ENTER_PHONE_NUMBER]: {
            content: (
                <StepEnterPhoneNumber
                    countryCode={countryCode}
                    phoneNumber={initialPhoneNumber}
                    submitInProgress={saveContactDetailsInProgress}
                    phoneSubmitError={savePhoneNumberError || sendVerificationOtpError}
                    missingPhoneNumberDescriptionId={missingPhoneNumberDescriptionId}
                    handleSubmitPhoneNumberForm={handleSubmitPhoneNumberForm}
                />
            ),
        },
        [CONFIRM_PHONE_NUMBER]: {
            content: (
                <StepConfirmPhoneNumber
                    otpCode={otpCode}
                    countryCode={countryCode}
                    currentPhoneNumber={currentPhoneNumber}
                    submitInProgress={saveContactDetailsInProgress}
                    otpError={sendVerificationOtpError}
                    phoneVerificationError={phoneVerificationError}
                    verifyPhoneNumber={verifyPhoneNumber}
                    handleReSendOtp={handleReSendOtp}
                    setVerificationState={setVerificationState}
                />
            ),
        },
        [PHONE_NUMBER_CONFIRMED]: {
            content: <StepPhoneNumberConfirmed onClose={onClose} />,
        },
    };

    const handleClose =
        redirectOnCLose && (!currentPhoneNumber || !currentPhoneNumberVerified)
            ? handleCompleteProfileDismiss
            : onClose;

    const modalComponent = (
        <ModalPortal
            id={id}
            containerClassName={css.modalContainer}
            contentClassName={css.modalContent}
            containerClassNameJoined
            isOpen={isOpen}
            onClose={handleClose}
        >
            {stateConfig[verificationState].content}
        </ModalPortal>
    );

    return modalComponent;
};

export default MissingPhoneNumberModal;
